import { CalendarFeedPlatformGrid } from '@/components/CalendarFeedPlatformGrid';
import type { CalendarFeedPlatform } from '@/components/CalendarFeedPlatformGrid/calendarFeedPlatform.lib';
import { useCurrentUserContext } from '@/contexts';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { getFeedUrl } from '@/features/interactions/subscribe/feedUrl';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Box, Button, Divider, Flex, Icon, Text, useToast } from '@/ui';

// No "Other calendar": it only copies the plain feed URL, which the copy box
// above already does. The modal keeps it, having no URL box of its own.
const FEED_PLATFORMS: CalendarFeedPlatform[] = [
  'apple',
  'google',
  'outlook',
  'office',
];

type Props = {
  experienceId: string;
};

// The SYNCS feed: subscribe to the whole schedule with one link. Paste it into
// any calendar app's "add by URL" — it stays in sync as the schedule changes.
export const FeedExportView = ({ experienceId }: Props) => {
  const toast = useToast();
  const { trackEvent } = useAnalytics();
  const { isAuthenticated } = useCurrentUserContext();
  const { readOnly } = usePageExperienceContext();
  const feedUrl = getFeedUrl(experienceId);

  const handleCopy = async () => {
    // navigator.clipboard is undefined in insecure contexts and writeText can
    // reject when permission is denied, so guard both and only celebrate on a
    // real success — otherwise point the user at the visible link to copy by hand.
    try {
      await navigator.clipboard.writeText(feedUrl);
      toast.notify('✅ Calendar feed link copied!');
    } catch {
      toast.error('Couldn’t copy the link. Select it above to copy manually.');
    }
  };

  return (
    <Flex direction="column" gap={3}>
      <Flex
        align="flex-start"
        borderColor="brand.500"
        borderRadius="ah-lg"
        borderStyle="solid"
        borderWidth="1px"
        gap={3}
        px={4}
        py={3.5}
      >
        <Icon color="ink.strong" icon="Rss" mt="2px" size={5} />
        <Box>
          <Text color="ink.strong" fontSize="ah-row" fontWeight={700}>
            Subscribe with a calendar feed
          </Text>
          <Text color="ink.muted" fontSize="ah-status" lineHeight={1.4} mt={1}>
            Paste the link below into your calendar app. It keeps every event in
            sync. Updates can take up to 48 hours to land, but most often within
            1 day.
          </Text>
        </Box>
      </Flex>

      <Flex align="center" gap={2}>
        <Box
          bg="surface.panel"
          borderColor="line.panel"
          borderRadius="ah-md"
          borderStyle="solid"
          borderWidth="1px"
          color="ink.soft"
          flex="1"
          fontSize="ah-status"
          minW={0}
          overflowX="auto"
          px={3}
          py={2}
          whiteSpace="nowrap"
        >
          {feedUrl}
        </Box>
        <Button
          flexShrink={0}
          leftIcon={<Icon icon="Copy" size={4} />}
          size="ah"
          variant="ahBrand"
          onClick={handleCopy}
        >
          Copy
        </Button>
      </Flex>

      <Text color="ink.muted" fontSize="ah-status" lineHeight={1.4}>
        In your calendar app, choose “Add by URL” and paste this link.
      </Text>

      <Divider />

      {/* The heading names the goal rather than promising one click: Apple,
          Outlook and Office open a subscribe flow, but Google only copies. */}
      <Box>
        <Text textStyle="ahLabel">Add the feed to your calendar app</Text>
        <CalendarFeedPlatformGrid
          isCompact
          platforms={FEED_PLATFORMS}
          url={feedUrl}
          onPlatformClick={(platform) => {
            // Same event the SubscribeFeedModal fires, so the two entry points
            // into the feed stay comparable.
            trackEvent('interaction:add-event click subscribe-all', {
              channel: platform === 'google' ? 'google-feed' : platform,
              experienceId,
              isAuthenticated,
              mode: readOnly ? 'view' : 'edit',
            });
          }}
        />
      </Box>
    </Flex>
  );
};
